import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Button } from '@/components/ui/button';
import { Sparkles, Building2, MapPin, MessageCircle, ExternalLink, Globe, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrentMatchups, MatchupWithPartner } from '@/hooks/useMatchups';
import { format } from 'date-fns';

function MatchupCard({ matchup, showWeek }: { matchup: MatchupWithPartner; showWeek?: boolean }) {
  const navigate = useNavigate();
  const partner = matchup.partner_profile;

  return (
    <Card className="card-qbn">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <Avatar className="h-14 w-14 flex-shrink-0">
            <SignedAvatarImage src={partner?.avatar_url || undefined} />
            <AvatarFallback className="bg-gradient-primary text-white font-medium text-lg">
              {partner?.full_name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground">{partner?.full_name ?? 'QBN Member'}</h3>
            {partner?.pronouns && (
              <span className="text-xs text-muted-foreground">({partner.pronouns})</span>
            )}
            {partner?.headline && (
              <p className="text-sm text-muted-foreground mt-0.5">{partner.headline}</p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              {partner?.company && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {partner.company}
                </span>
              )}
              {partner?.city_cohort && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {partner.city_cohort}
                </span>
              )}
              {partner?.industry && (
                <span className="flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5" />
                  {partner.industry}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* AI Reason */}
        <div className="mt-3 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm text-foreground leading-relaxed">{matchup.ai_reason}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3">
          <Button
            size="sm"
            variant="outline"
            className="text-primary border-primary/20 hover:bg-primary/10"
            onClick={() => navigate(`/messages?to=${partner?.user_id}`)}
          >
            <MessageCircle className="h-4 w-4 mr-1" />
            Message
          </Button>
          {partner?.linkedin_url && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" asChild>
              <a href={partner.linkedin_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                LinkedIn
              </a>
            </Button>
          )}
        </div>

        {showWeek && matchup.round_week_of && (
          <p className="text-xs text-muted-foreground mt-3">
            Week of {format(new Date(matchup.round_week_of), 'MMM d, yyyy')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function MatchupsPage() {
  const { currentMatchups, pastMatchups, isLoading } = useCurrentMatchups();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Group past matchups by week
  const pastByWeek = pastMatchups.reduce<Record<string, MatchupWithPartner[]>>((acc, m) => {
    const week = m.round_week_of;
    if (!acc[week]) acc[week] = [];
    acc[week].push(m);
    return acc;
  }, {});

  const pastWeeks = Object.keys(pastByWeek).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Matchups</h1>
        <p className="text-muted-foreground text-sm">
          Weekly AI-powered connections with fellow QBN members
        </p>
      </div>

      {currentMatchups.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-primary">This Week's Matches</h2>
          </div>
          {currentMatchups.map((m) => (
            <MatchupCard key={m.id} matchup={m} />
          ))}
        </div>
      ) : (
        <Card className="card-qbn">
          <CardContent className="p-8 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="font-semibold text-foreground mb-2">No matchups yet</h3>
            <p className="text-sm text-muted-foreground">
              Matchups are generated weekly. Make sure you have "Open to Matchups" enabled in your profile settings.
            </p>
          </CardContent>
        </Card>
      )}

      {pastWeeks.map((week) => (
        <div key={week} className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">
            Week of {format(new Date(week), 'MMM d, yyyy')}
          </h2>
          {pastByWeek[week].map((m) => (
            <MatchupCard key={m.id} matchup={m} />
          ))}
        </div>
      ))}
    </div>
  );
}
